import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable} from 'rxjs';
import {
    AccessType,
    AuthService,
    DataSources,
    ExportMyScoreOperationsQuery,
    GetMyScoreOperationsL1ScoreQuery,
    GetMyScoreOperationsL2ScoreQuery,
    GetMyScoreOperationsL3ScoreQuery,
    InvoicingClient,
    MyScoreOperationsScoreDto,
    PermissionType,
} from '@services';
import {FileUtils} from '../../../../shared/file-utils';
import {DoughnutChartTableComponent, DoughnutChartTableSlice} from '../../../../components/library/doughnut-chart-table/doughnut-chart-table.component';
import {MyScoreFilterBarComponent, MyScoreFilters} from '../my-score-filter-bar/my-score-filter-bar.component';

type OperationsSection = {
    title: string;
    load: (filters: MyScoreFilters) => Observable<MyScoreOperationsScoreDto>;
    slices: DoughnutChartTableSlice[];
    score: number | null;
};

@Component({
    selector: 'app-my-score-operations',
    imports: [
        CommonModule,
        DoughnutChartTableComponent,
        MyScoreFilterBarComponent,
    ],
    templateUrl: './my-score-operations.component.html',
})
export class MyScoreOperationsComponent implements OnInit {
    private invoicingClient = inject(InvoicingClient);
    private dataSources = inject(DataSources);
    private auth = inject(AuthService);
    private destroyRef = inject(DestroyRef);

    private static readonly CategoryLabels = {
        pendingUnder24: 'Pending Approval <24',
        pendingOver24: 'Pending Approval >24',
        approved: 'Approved',
    };

    protected palette: Record<string, string> = {};

    protected vendorDataSource = this.dataSources.vendorOptions();
    protected siteDataSource = this.dataSources.siteOptions(PermissionType.MyScoreOperationsL1);

    protected sections: OperationsSection[] = [];

    private filters?: MyScoreFilters;

    private readonly sectionConfigs: { title: string; permission: PermissionType; load: (filters: MyScoreFilters) => Observable<MyScoreOperationsScoreDto> }[] = [
        {
            title: 'L1 Approval',
            permission: PermissionType.MyScoreOperationsL1,
            load: filters => this.invoicingClient.getMyScoreOperationsL1Score(new GetMyScoreOperationsL1ScoreQuery(filters)),
        },
        {
            title: 'L2 Approval',
            permission: PermissionType.MyScoreOperationsL2,
            load: filters => this.invoicingClient.getMyScoreOperationsL2Score(new GetMyScoreOperationsL2ScoreQuery(filters)),
        },
        {
            title: 'L3 Approval',
            permission: PermissionType.MyScoreOperationsL3,
            load: filters => this.invoicingClient.getMyScoreOperationsL3Score(new GetMyScoreOperationsL3ScoreQuery(filters)),
        },
    ];

    ngOnInit(): void {
        const labels = MyScoreOperationsComponent.CategoryLabels;
        const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
        this.palette = {
            [labels.pendingUnder24]: getColor('--bs-info'),
            [labels.pendingOver24]: getColor('--bs-warning'),
            [labels.approved]: getColor('--bs-success'),
        };

        this.sections = this.sectionConfigs
            .filter(config => this.auth.checkPermission(config.permission, AccessType.ViewOnly))
            .map(config => ({title: config.title, load: config.load, slices: [], score: null}));
    }

    protected onFiltersChanged(filters: MyScoreFilters): void {
        this.filters = filters;
        this.loadScores();
    }

    protected onExport(): void {
        if (!this.filters) return;

        this.invoicingClient
            .exportMyScoreOperations(new ExportMyScoreOperationsQuery(this.filters))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(res => FileUtils.downloadFile(res));
    }

    private loadScores(): void {
        if (!this.filters) return;

        const labels = MyScoreOperationsComponent.CategoryLabels;
        for (const section of this.sections) {
            section.load(this.filters)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(dto => {
                    section.slices = [
                        {argument: labels.pendingUnder24, value: dto.pendingUnder24 ?? 0},
                        {argument: labels.pendingOver24, value: dto.pendingOver24 ?? 0},
                        {argument: labels.approved, value: dto.approved ?? 0},
                    ];
                    section.score = dto.myScorePercent ?? null;
                });
        }
    }
}
